import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { LxConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { setCookie } from "components/utility/cookie";
import { LastSearchData, LXLastSearchData } from "stores/wizard/LastSearchData";

export class LxWizardState implements LOBState {
  private globalState: GlobalWizardState;
  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB;
  }

  public lastSearchData?: LXLastSearchData;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public config: LxConfig;

  @observable
  public subLOBState: string;

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  @observable
  public numberOfErrors = 0;

  public moduleName: string = "wizard-lx-pwa";

  @observable
  public trackingEnabled: boolean = true;

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @computed
  public get date() {
    return this.globalState.date;
  }

  // validations
  @observable
  public destinationInvalidKey: string | "";

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateDestinationField = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;
      this.errorInputRef = this.findFocusElement("destination")!;

      return false;
    }

    this.destinationInvalidKey = "";

    return true;
  };

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @action
  public validateMaxDateRange = () => {
    if (!this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)) {
      this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")!;
      }

      return false;
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  public validateForm = () => {
    this.cleanErrorState();
    const validations: boolean[] = [this.validateDestinationField(), this.validateEndDateField()];
    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const destination = this.location.destination;
      const destinationMetaData = this.location.destination.metaData;
      const date = this.date;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        lx: {
          destination: {
            value: destination.value || "",
            regionID: destinationMetaData.regionId || "",
            ttla: destinationMetaData.ttla || "",
            countryCode: destinationMetaData.countryCode || "",
            latLong: destinationMetaData.latLong || "",
            destinationID: destinationMetaData.destinationId || "",
            hotelID: destinationMetaData.hotelId || "",
          },
          date: {
            start: date.start ? date.start.toString() : "",
            end: date.end ? date.end.toString() : "",
          },
          timestamp: Math.floor(Date.now() / 1000),
        },
      };

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
      });
    }
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string,
    timestamp?: number
  ) {
    this.globalState.updateDestinationFromCookie(
      value,
      regionID,
      countryCode,
      latLong,
      ttla,
      destinationID,
      hotelID,
      timestamp
    );
    this.destinationInvalidKey = "";
  }

  public setDefaultDestination = (
    destinationValue: string,
    regionID: string,
    countryCode: string,
    ttla: string,
    destinationID: string,
    hotelID: string
  ) => {
    if (typeof this.lastSearchData === "undefined") {
      this.updateDestinationFromCookie(destinationValue, regionID, countryCode, "", ttla, destinationID, hotelID);
    }
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const destinationValue = decodeURIComponent(lastSearchData.destination?.value);
      const startDateFromCookie = lastSearchData.date?.start ? new Date(lastSearchData.date.start) : new Date();
      const endDateFromCookie = lastSearchData.date?.end ? new Date(lastSearchData.date.end) : new Date();
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        lastSearchData.destination?.regionID ? lastSearchData.destination.regionID : "",
        lastSearchData.destination?.countryCode ? lastSearchData.destination.countryCode : "",
        lastSearchData.destination?.latLong ? lastSearchData.destination.latLong : "",
        lastSearchData.destination?.ttla ? lastSearchData.destination.ttla : "",
        lastSearchData.destination?.destinationID ? lastSearchData.destination.destinationID : "",
        lastSearchData.destination?.hotelID ? lastSearchData.destination.hotelID : "",
        lastSearchData.timestamp
      );
      this.updateDateSelection(startDateToUse, endDateToUse);
    }
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
      this.errorSummaryRef = this.findFocusElement("error")!;
      this.errorSummaryRef?.current?.focus();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
    }
  };

  /**
   * Cleans error state in all forms
   */

  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  constructor(config: LxConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.lx;
    }
  }
}
